import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { toast } from 'sonner'
import { AlertTriangle, Plus, RefreshCw, Settings2 } from 'lucide-react'
import {
  Button,
  Chip,
  EmptyState,
  groupRows,
  MetricCard,
  MetricStrip,
  PageHeaderBand,
  SearchInput,
  Select,
  Toolbar,
  ToolbarLeft,
  ToolbarRight,
  useTableGrouping,
  ViewToggle,
  type TableGroup,
} from '@/components/ui'
import { useHasAnyPermission } from '@/shell/module-system'
import {
  useMoveStage,
  useReorderStages,
  useStages,
  useSupportStats,
  useTicketList,
} from './api/queries'
import type { Assignee, Category, Priority, TicketSummary } from './api/types'
import {
  accentForHex,
  ASSIGNEE_ORDER,
  ASSIGNEE_SHORT,
  CATEGORY_OPTIONS,
  CATEGORY_SHORT,
  PRIORITY_LABELS,
  PRIORITY_OPTIONS,
} from './utils/meta'
import { SupportKanban } from './components/SupportKanban'
import { SupportTable } from './components/SupportTable'
import { NewTicketDrawer } from './components/NewTicketDrawer'
import { StageManagerDrawer } from './components/StageManagerDrawer'

type View = 'board' | 'archive'
type Display = 'kanban' | 'table'
type AssigneeFilter = '' | Assignee | 'unassigned'

const VIEW_OPTIONS = [
  { value: 'board' as const, label: 'Board' },
  { value: 'archive' as const, label: 'Archive' },
]

const DISPLAY_OPTIONS = [
  { value: 'kanban' as const, label: 'Kanban' },
  { value: 'table' as const, label: 'Table' },
]

const GROUP_BY_OPTIONS = [
  { value: 'stage', label: 'Group by stage' },
  { value: 'assignee', label: 'Group by assignee' },
  { value: 'category', label: 'Group by category' },
  { value: 'priority', label: 'Group by priority' },
  { value: 'none', label: 'No grouping' },
]

const PRIORITY_GROUP_ORDER: Priority[] = ['urgent', 'high', 'medium', 'low']

/**
 * Customer Support Command & Control board. Single Kanban (no request_type
 * split), SLA-breach banner + assignee quick-filter tabs (spec §9.1), a
 * detail drawer, new-ticket + stage-manager drawers.
 */
export function SupportBoard() {
  const canEdit = useHasAnyPermission([
    'admin', 'operations', 'purchasing', 'sales', 'regulatory', 'finance',
  ])

  const navigate = useNavigate()
  const [view, setView] = useState<View>('board')
  const [display, setDisplay] = useState<Display>('kanban')
  const { groupBy, setGroupBy, collapsedGroups, toggleGroup } = useTableGrouping(
    'support',
    'stage',
  )
  const [assignee, setAssignee] = useState<AssigneeFilter>('')
  const [category, setCategory] = useState<'' | Category>('')
  const [priority, setPriority] = useState<'' | Priority>('')
  const [breachedOnly, setBreachedOnly] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const [newOpen, setNewOpen] = useState(false)
  const [stageMgrOpen, setStageMgrOpen] = useState(false)

  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(search), 200)
    return () => clearTimeout(id)
  }, [search])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === '/' && e.target instanceof HTMLElement) {
        const typing =
          e.target.tagName === 'INPUT' ||
          e.target.tagName === 'TEXTAREA' ||
          e.target.isContentEditable
        if (typing) return
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const openTicket = (id: string) =>
    navigate({ to: '/support/$ticketId', params: { ticketId: id } })

  const isArchive = view === 'archive'
  const stats = useSupportStats()
  const stages = useStages()
  const move = useMoveStage()
  const reorder = useReorderStages()

  const list = useTicketList({
    ...(isArchive ? { is_archived: true } : {}),
    ...(assignee ? { assigned_to: assignee } : {}),
    ...(category ? { category } : {}),
    ...(priority ? { priority } : {}),
    ...(breachedOnly ? { sla_breached: true } : {}),
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
  })

  const items = useMemo(() => list.data?.items ?? [], [list.data])
  const breachedCount = stats.data?.sla_breached ?? 0
  const stageItems = useMemo(
    () => [...(stages.data?.items ?? [])].sort((a, b) => a.sequence - b.sequence),
    [stages.data],
  )

  // Table-view grouping. Stage uses the full ordered stage list so empty
  // stages still render a `(0)` header (matches the kanban columns).
  const tableGroups = useMemo<TableGroup<TicketSummary>[] | undefined>(() => {
    if (display !== 'table' || isArchive || groupBy === 'none') return undefined
    if (groupBy === 'stage') {
      const byId = new Map(stageItems.map((s) => [s.id, s] as const))
      return groupRows(items, {
        keyOf: (c) => c.stage_id ?? '',
        groupOrder: stageItems.map((s) => s.id),
        labelOf: (k) => byId.get(k)?.name ?? '(no stage)',
        accentOf: (k) => accentForHex(byId.get(k)?.color ?? null),
      })
    }
    if (groupBy === 'assignee') {
      return groupRows(items, {
        keyOf: (c) => c.assigned_to_name ?? '',
        labelOf: (k) => (k === '__none__' ? 'Unassigned' : k),
      })
    }
    if (groupBy === 'category') {
      return groupRows(items, {
        keyOf: (c) => c.category,
        labelOf: (k) => CATEGORY_SHORT[k as Category] ?? k,
      })
    }
    // priority
    return groupRows(items, {
      keyOf: (c) => c.priority,
      groupOrder: PRIORITY_GROUP_ORDER,
      labelOf: (k) => PRIORITY_LABELS[k as Priority] ?? k,
    })
  }, [display, isArchive, groupBy, items, stageItems])

  return (
    <div className="flex h-full w-full flex-col bg-canvas">
      <PageHeaderBand title="Customer Support" description="support@siliconesplus.com — 1 business-day SLA">
        <div className="ml-auto">
          <MetricStrip variant="inline">
            <MetricCard variant="inline" label="Open" value={stats.data?.open_total ?? 0} />
            <MetricCard variant="inline" label="SLA breached" value={breachedCount} />
          </MetricStrip>
        </div>
      </PageHeaderBand>

      {/* SLA breach banner (spec §9.1) */}
      {!isArchive && breachedCount > 0 ? (
        <div className="flex items-center gap-2 border-b border-border-subtle bg-danger-subtle px-6 py-2 text-sm text-danger">
          <AlertTriangle className="h-4 w-4" />
          <span className="font-medium">
            {breachedCount} ticket{breachedCount === 1 ? '' : 's'} breached the 1-business-day SLA
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() => setBreachedOnly(true)}
          >
            View breached
          </Button>
        </div>
      ) : null}

      <Toolbar>
        <SearchInput
          ref={searchRef}
          containerClassName="flex-1 max-w-none"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search ticket #, title, customer…"
        />
      </Toolbar>
      <Toolbar>
        <ToolbarLeft>
          <ViewToggle<View> options={VIEW_OPTIONS} value={view} onChange={setView} />
          {!isArchive ? (
            <ViewToggle<Display>
              options={DISPLAY_OPTIONS}
              value={display}
              onChange={setDisplay}
              aria-label="Display mode"
            />
          ) : null}
          {!isArchive && display === 'table' ? (
            <Select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              aria-label="Group rows by"
            >
              {GROUP_BY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          ) : null}
          <Select
            value={category}
            onChange={(e) => setCategory(e.target.value as typeof category)}
            aria-label="Filter by category"
          >
            <option value="">All categories</option>
            {CATEGORY_OPTIONS.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <Select
            value={priority}
            onChange={(e) => setPriority(e.target.value as typeof priority)}
            aria-label="Filter by priority"
          >
            <option value="">All priorities</option>
            {PRIORITY_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-1.5 text-role-toolbar text-secondary">
            <input
              type="checkbox"
              checked={breachedOnly}
              onChange={(e) => setBreachedOnly(e.target.checked)}
            />
            SLA breached
          </label>
        </ToolbarLeft>
        <ToolbarRight>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void list.refetch()
              void stats.refetch()
            }}
            aria-label="Refresh"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canEdit && !isArchive ? (
            <Button variant="secondary" size="sm" onClick={() => setStageMgrOpen(true)}>
              <Settings2 className="mr-1 h-4 w-4" />
              Stages
            </Button>
          ) : null}
          {canEdit ? (
            <Button variant="primary" size="sm" onClick={() => setNewOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />
              New Ticket
            </Button>
          ) : null}
        </ToolbarRight>
      </Toolbar>

      {/* Assignee quick-filter tabs (spec §9.1) */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-border-subtle bg-surface-sunk px-6 py-2">
        <AssigneeTab label="All" active={assignee === ''} onClick={() => setAssignee('')} />
        {ASSIGNEE_ORDER.map((a) => (
          <AssigneeTab
            key={a}
            label={ASSIGNEE_SHORT[a]}
            active={assignee === a}
            onClick={() => setAssignee(a)}
          />
        ))}
        <AssigneeTab
          label="Unassigned"
          active={assignee === 'unassigned'}
          onClick={() => setAssignee('unassigned')}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {isArchive ? (
          items.length === 0 ? (
            <EmptyState
              title="No archived tickets"
              description="Resolved, cancelled, and noise tickets land here."
            />
          ) : (
            <ArchiveList items={items} onSelect={(c) => openTicket(c.id)} />
          )
        ) : display === 'table' ? (
          <SupportTable
            rows={items}
            stages={stageItems}
            groups={tableGroups}
            collapsedGroups={collapsedGroups}
            onToggleGroup={toggleGroup}
            onSelect={(c) => openTicket(c.id)}
          />
        ) : (
          <SupportKanban
            stages={stages.data?.items ?? []}
            items={items}
            onSelect={(c) => openTicket(c.id)}
            onMove={(card, targetStageId) =>
              move.mutate(
                { id: card.id, targetStageId },
                { onError: (e) => toast.error(String(e)) },
              )
            }
            onReorder={(stageIds) =>
              reorder.mutate(stageIds.map((id, i) => ({ id, sequence: (i + 1) * 10 })))
            }
          />
        )}
      </div>

      <NewTicketDrawer open={newOpen} onOpenChange={setNewOpen} onCreated={(id) => openTicket(id)} />
      <StageManagerDrawer open={stageMgrOpen} onOpenChange={setStageMgrOpen} />
    </div>
  )
}

function AssigneeTab({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-role-toolbar transition-colors ${
        active
          ? 'bg-accent text-on-accent'
          : 'bg-surface text-secondary hover:bg-surface-raised'
      }`}
    >
      {label}
    </button>
  )
}

function ArchiveList({
  items,
  onSelect,
}: {
  items: TicketSummary[]
  onSelect: (c: TicketSummary) => void
}) {
  return (
    <div className="flex flex-col gap-1">
      {items.map((c) => (
        <button
          key={c.id}
          onClick={() => onSelect(c)}
          className="flex items-center gap-3 rounded-sm border border-border-subtle bg-surface px-3 py-2 text-left hover:border-border-strong"
        >
          <span className="text-role-metadata text-tertiary w-28 shrink-0">
            {c.ticket_number}
          </span>
          <span className="text-role-data-primary flex-1 truncate">{c.title}</span>
          <Chip size="xs" variant="neutral">
            {c.assigned_to_name ?? 'Unassigned'}
          </Chip>
          <span className="text-role-metadata text-tertiary">{c.partner_name}</span>
        </button>
      ))}
    </div>
  )
}
